export interface IDialogRegisteredEventDetail {
    dialogId: string;
    dialogHandler: ManagedDialogHandler;
}

export class ManagedDialogHandler {
    static readonly DialogRegisteredEventName: string = 'registered';

    private readonly dialog: HTMLDialogElement;
    private readonly closeDialogButton: HTMLButtonElement | null;
    private readonly showDialogButton: HTMLButtonElement | null;
    private readonly preventBackdropClose: boolean;

    private readonly clickDialogFunc: (event: MouseEvent) => void;
    private readonly closeDialogFunc: () => void;
    private readonly showDialogFunc: () => void;

    constructor(
        dialogId: string,
        closeDialogButtonSelector?: string,
        showDialogButtonSelector?: string,
        preventBackdropClose: boolean = false
    ) {
        const dialogElement: HTMLElement | null =
            (document.getElementById(dialogId) as HTMLElement) ?? null;
        if (!dialogElement) {
            console.warn(`Dialog with ID "${dialogId}" not found`);
            throw new Error(`Dialog element not found: ${dialogId}`);
        }

        this.dialog = dialogElement as HTMLDialogElement;

        this.closeDialogButton = closeDialogButtonSelector
            ? ((document.querySelector(
                  closeDialogButtonSelector
              ) as HTMLButtonElement) ?? null)
            : null;

        this.showDialogButton = showDialogButtonSelector
            ? ((document.querySelector(
                  showDialogButtonSelector
              ) as HTMLButtonElement) ?? null)
            : null;

        this.preventBackdropClose = preventBackdropClose;
        this.clickDialogFunc = this.clickDialog.bind(this);
        this.closeDialogFunc = this.closeDialog.bind(this);
        this.showDialogFunc = this.showDialog.bind(this);

        this.initializeHandlers();
    }

    private clickDialog(event: MouseEvent): void {
        if (event.target === this.dialog && !this.preventBackdropClose) {
            this.dialog.close();
        }
    }

    /**
     * Check if the dialog is currently open
     * @returns boolean indicating whether the dialog is open
     */
    isOpen(): boolean {
        return this.dialog.open;
    }

    private closeDialog(): void {
        this.dialog.close();
    }

    private showDialog(): void {
        this.dialog.showModal();

        this.dialog.dispatchEvent(
            new CustomEvent(ManagedDialogHandler.DialogRegisteredEventName, {
                detail: {
                    dialogId: this.dialog.id,
                    dialogHandler: this
                } satisfies IDialogRegisteredEventDetail,
                bubbles: true
            })
        );
    }

    private initializeHandlers() {
        // Close dialog when close button is clicked
        this.closeDialogButton?.addEventListener('click', this.closeDialogFunc);

        // Close the dialog when clicking outside (on dialog backdrop), unless prevented
        this.dialog.addEventListener('click', this.clickDialogFunc);

        // Show dialog when the show button is clicked
        this.showDialogButton?.addEventListener('click', this.showDialogFunc);
    }

    // Methods to programmatically show/hide
    show() {
        this.showDialog();
    }

    close() {
        this.closeDialog();
    }
}

// "export" through the window global object for JavaScript compatibility
(window as any)['ManagedDialogHandler'] = ManagedDialogHandler;
